<template>
  <div
    v-if="props.sections.length"
    :class="DS_EMOJI_PICKER_CLASSES.Grid"
    data-cy="ds-emoji-picker__grid"
    :aria-keyshortcuts="props.hasMoreEmojis ? EMOJI_PICKER_LOAD_MORE_KEY : undefined"
    @scroll="onScroll"
    @keydown="onKeydown"
  >
    <section
      v-for="section in props.sections"
      :key="section.category"
      :class="DS_EMOJI_PICKER_CLASSES.Section"
      :aria-labelledby="sectionHeadingId(section.category)"
      :data-cy="`ds-emoji-picker__section--${section.category}`"
    >
      <h3
        :id="sectionHeadingId(section.category)"
        :class="DS_EMOJI_PICKER_CLASSES.SectionHeading"
        :data-cy="`ds-emoji-picker__section-heading--${section.category}`"
      >
        {{ getEmojiPickerCategoryLabel(section.category) }}
      </h3>

      <div :class="DS_EMOJI_PICKER_CLASSES.SectionGrid">
        <DSButton
          v-for="emoji in section.emojis"
          :key="emoji.unicode"
          :variant="DS_BUTTON_VARIANT.Ghost"
          :size="DS_SIZE.Small"
          :aria-label="emoji.aliases[0]"
          :aria-keyshortcuts="props.hasMoreEmojis ? EMOJI_PICKER_LOAD_MORE_KEY : undefined"
          :data-cy="`ds-emoji-picker__item--${emoji.unicode}`"
          @mouseenter="emit('hover', emoji)"
          @focus="emit('hover', emoji)"
          @click="emit('select', emoji)"
        >
          {{ emoji.native }}
        </DSButton>
      </div>
    </section>
  </div>

  <div
    v-else
    :class="DS_EMOJI_PICKER_CLASSES.Empty"
    data-cy="ds-emoji-picker__empty"
  >
    {{ EMPTY_STATE }}
  </div>
</template>

<script setup lang="ts">
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { DS_BUTTON_VARIANT } from "@/src/design_system/button_and_controls/button/types/props";
import { DS_EMOJI_PICKER_CLASSES } from "@/src/design_system/button_and_controls/emoji_picker/constants/classes";
import {
  EMOJI_PICKER_LOAD_MORE_KEY,
  EMOJI_PICKER_LOAD_MORE_SCROLL_OFFSET_PX,
} from "@/src/design_system/button_and_controls/emoji_picker/constants/pagination";
import type { EmojiPickerSection } from "@/src/design_system/button_and_controls/emoji_picker/types/panel";
import { getEmojiPickerCategoryLabel } from "@/src/design_system/button_and_controls/emoji_picker/utils/categoryLabels";
import { DS_SIZE } from "@/src/design_system/shared/types/size";
import type { EmojiCategory, EmojiDisplayItem } from "@/src/shared/emoji/types";

const props = defineProps<{
  id: string;
  sections: EmojiPickerSection[];
  hasMoreEmojis: boolean;
}>();

const emit = defineEmits<{
  (e: "hover", emoji: EmojiDisplayItem): void;
  (e: "select", emoji: EmojiDisplayItem): void;
  (e: "load-more"): void;
}>();

const EMPTY_STATE = window.I18n.designSystem.emojiPicker.emptyState;

const sectionHeadingId = (category: EmojiCategory): string => {
  return `${props.id}-section-${category}`;
};

const onScroll = (event: Event): void => {
  if (!props.hasMoreEmojis) return;

  const grid = event.currentTarget as HTMLElement;
  const remainingScroll = grid.scrollHeight - grid.scrollTop - grid.clientHeight;
  if (remainingScroll <= EMOJI_PICKER_LOAD_MORE_SCROLL_OFFSET_PX) emit("load-more");
};

const onKeydown = (event: KeyboardEvent): void => {
  if (!props.hasMoreEmojis || event.key !== EMOJI_PICKER_LOAD_MORE_KEY) return;

  event.preventDefault();
  emit("load-more");
};
</script>
